import React from "react";
import { Plus } from "lucide-react";
import { Card } from "../../../../shared/card/card.tsx";
import {
  TimesheetEntryForm,
  type TimesheetEntryFormValues,
} from "../../forms/timesheet-entry-form/timesheet-entry-form.tsx";
import { useTimesheetOptions } from "../../hooks/use-timesheet-options.ts";
import { TimesheetEntry } from "../../types.ts";
import type { ProjectAllocationRow } from "../../utils/timesheet-allocation.ts";
import styles from "./add-new-entry-card.module.css";

const FORM_ID = "timesheet-add-entry-form";

type Props = {
  selectedDate: Date;
  onAddEntry: (entry: TimesheetEntry) => void;
  projectAllocations?: ProjectAllocationRow[];
  getOtherEntriesMinutesForProject?: (projectId: number) => number;
};


const INITIAL_VALUES: TimesheetEntryFormValues = {
  project: "",
  workStreamId: "",
  phase: "",
  category: "",
  description: "",
  hours: "",
  minutes: "00",
  status: "",
};

export const AddNewEntryCard = ({
  selectedDate,
  onAddEntry,
  projectAllocations,
  getOtherEntriesMinutesForProject,
}: Props) => {
  const { categories, phases, projects, projectWorkstreamsMap, loading, categoriesLoading, loadProjectCategories } =
    useTimesheetOptions(selectedDate);

  const handleSave = (values: TimesheetEntryFormValues) => {
    const isMiscellaneous = values.project === "MISCELLANEOUS";
    const selectedProject = projects.find((p) => p.value === values.project);
    const selectedWorkStream = projectWorkstreamsMap[values.project]?.find(
      (stream) => String(stream.workStreamId) === values.workStreamId,
    );
    const selectedCategory = categories.find((category) => category.value === values.category);
    const selectedPhase = phases.find((phase) => phase.value === values.phase);

    const entry: TimesheetEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      projectId: isMiscellaneous ? null : Number(values.project),
      project: selectedProject?.label ?? values.project,
      category: selectedCategory?.label ?? values.category,
      categoryId: isMiscellaneous ? null : Number(values.category),
      phase: isMiscellaneous || selectedPhase?.id ? null : values.phase || null,
      phaseId: isMiscellaneous ? null : selectedPhase?.id ?? null,
      phaseCode: isMiscellaneous ? null : (selectedPhase?.code ?? (values.phase || null)),
      phaseName: isMiscellaneous ? null : selectedPhase?.label ?? null,
      description: values.description,
      hours: Number(values.hours) || 0,
      minutes: values.minutes || "00",
      status: values.status,
      workStream: selectedWorkStream?.workStream,
      workStreamId: selectedWorkStream?.workStreamId ?? null,
      date: selectedDate.toISOString().split("T")[0],
      allocationId: null,
      titleType: isMiscellaneous ? "OTHERS" : "PROJECT",
    };
    onAddEntry(entry);
  };

  return (
    <Card className={styles.timesheetEntryCard}>
      <TimesheetEntryForm
        key={selectedDate.toDateString()}
        formId={FORM_ID}
        initial={INITIAL_VALUES}
        projects={projects}
        categories={categories}
        phases={phases}
        projectWorkstreamsMap={projectWorkstreamsMap}
        optionsLoading={loading}
        categoriesLoading={categoriesLoading}
        onProjectChange={loadProjectCategories}
        onWorkstreamChange={loadProjectCategories}
        onSave={handleSave}
        showAsterisks={true}
        projectAllocations={projectAllocations ?? []}
        getOtherEntriesMinutesForProject={getOtherEntriesMinutesForProject}
        resetFieldsOnSubmit={[
          "project",
          "workStreamId",
          "phase",
          "category",
          "description",
          "hours",
          "minutes",
          "status",
        ]}
        action={
          <button
            type="submit"
            className={`btn-xl ${styles.button} ms-auto`}
          >
            <Plus size={16} /> Add Entry
          </button>
        }
      />
    </Card>
  );
};
